import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import BusComponent from '../BusComponent';
import TextInput from '../form/TextInput';
import googleTagManagerEvents from '../../helpers/GoogleTagManagerEventsWrapper';
import {
    submitMissingFacilityEvents,
    submitEditedFacilityEvents,
} from '../../enums/GoogleTagManagerEvents';
import SimpleDropdown from '../dropdown/SimpleDropdown';
import LocationSuggestionInput from './LocationSuggestionInput';

const SubmitMissingFacilityButton = googleTagManagerEvents('button', submitMissingFacilityEvents);
const SubmitEditedFacilityButton = googleTagManagerEvents('button', submitEditedFacilityEvents);

const CATEGORIES = ['Storage Facilities', 'Housing Developments', 'Other'];

const STATUSES = [
    'Open',
    'Permanently closed',
    'Temporary closed',
    'Never existed',
    'Not opened to the public',
    'Moved to a new location',
    'Duplicate of another place',
    'Other',
];

const COORDINATE_PRECISION = 5;

const EDITABLE_FIELDS = [
    'category',
    'name',
    'location',
    'telephone',
    'website',
    'status',
    'details',
];

class SubmitFacilityPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);
        this.state = {
            ...props,
            status: STATUSES[0],
            userDetails: {},
            pickedSuggestion: null,
            pinAddress: '',
            locationEdited: false,
            ...props.initialValues,
        };
        this.onCategoryChanged = this.onChange.bind(this, 'category');
        this.onNameChanged = this.onChange.bind(this, 'name');
        this.onTelephoneChanged = this.onChange.bind(this, 'telephone');
        this.onWebsiteChanged = this.onChange.bind(this, 'website');
        this.onStatusChanged = this.onChange.bind(this, 'status');
        this.onDetailsChanged = this.onChange.bind(this, 'details');
    }

    componentDidMount() {
        this.bus.on('SUBMIT_FACILITY_DATA_CHANGE_ERROR', this.onError);
        this.bus.on('SUBMIT_FACILITY_DATA_CHANGE_SUCCESS', this.onSuccess);
        this.bus.once('USER_INFO_GET_SUCCESS', this.onUserInfo);
        this.emit('USER_INFO_GET_REQUEST', { source: this });

        const { pinPoint, editMode } = this.props;
        if (pinPoint && !editMode) {
            this.bindGluBusEvents({
                REVERSE_GEOCODE_RESPONSE: this.onPinReverseGeocode,
            });
            this.emit('REVERSE_GEOCODE_REQUEST', { point: pinPoint });
        }
    }

    componentWillUnmount() {
        this.bus.off('SUBMIT_FACILITY_DATA_CHANGE_ERROR', this.onError);
        this.bus.off('SUBMIT_FACILITY_DATA_CHANGE_SUCCESS', this.onSuccess);
        this.bus.off('USER_INFO_GET_SUCCESS', this.onUserInfo);
        this.unbindGluBusEvents();
    }

    onError = payload => {
        console.error(payload);
    };

    onSuccess = () => {
        this.setState({
            submittedSuccessfully: true,
            disabled: false,
        });
    };

    onPinReverseGeocode = ({ point, address }) => {
        // the response is a broadcast — only accept the one for our pin
        const { pinPoint } = this.props;
        if (point.lng !== pinPoint.lng || point.lat !== pinPoint.lat) return;
        const pinAddress = address ? address.label : '';
        // seeds the editable Location field, so the pin's address can be
        // corrected in place; a user edit — or a geocode that came back with
        // nothing — leaves whatever is already typed alone
        this.setState(state => ({
            pinAddress,
            location: !pinAddress || state.locationEdited ? state.location : pinAddress,
        }));
    };

    onLocationChanged = value => {
        // manual typing invalidates a previously picked suggestion —
        // the submitted coordinates fall back to the pin's
        this.setState({ location: value, pickedSuggestion: null, locationEdited: true });
    };

    onLocationSuggestionSelect = suggestion => {
        this.setState({
            location: suggestion.label,
            pickedSuggestion: suggestion,
            locationEdited: true,
        });
    };

    onUserInfo = (userInfo, source) => {
        if (source && this !== source) return;
        this.setState({
            userDetails: {
                email: userInfo.email,
                name: userInfo._name,
                id: userInfo._userId,
            },
        });
    };

    onClose = () => {
        this.bus.emit('CLOSE_MODAL');
    };

    onReset = () => {
        const { editMode } = this.props;
        if (editMode) {
            this.setState({
                ...this.state.existingFacility,
            });
        } else {
            this.setState(state => ({
                status: STATUSES[0],
                location: state.pinAddress || '',
                telephone: '',
                website: '',
                details: '',
                pickedSuggestion: null,
                locationEdited: false,
            }));
        }
    };

    // hands the form back to the map so a new pin can be dropped; the controller
    // reopens the modal with the picked point and these values restored
    onPickLocationOnMap = () => {
        const { pinPoint } = this.props;
        this.emit('PICK_MISSING_FACILITY_LOCATION_REQUEST', {
            pinPoint,
            values: EDITABLE_FIELDS.reduce((values, field) => {
                if (this.state[field] !== undefined) values[field] = this.state[field];
                return values;
            }, {}),
        });
    };

    // last action wins: a picked Location suggestion beats the pin captured at
    // form-open; with neither, no coordinates are sent
    resolveLocation() {
        const { pinPoint } = this.props;
        const { pickedSuggestion, pinAddress } = this.state;
        if (pickedSuggestion) {
            return {
                lat: pickedSuggestion.lat,
                lng: pickedSuggestion.lng,
                address: pickedSuggestion.label,
                fromPin: false,
            };
        }
        if (pinPoint) {
            return {
                lat: pinPoint.lat,
                lng: pinPoint.lng,
                address: pinAddress,
                fromPin: true,
            };
        }
        return undefined;
    }

    onSubmitFacility = () => {
        const { type } = this.props;
        const {
            userDetails,
            category,
            name,
            location,
            telephone,
            website,
            status,
            details,
            existingFacility,
        } = this.state;
        const resolved = this.resolveLocation();
        const geo = resolved
            ? {
                  latitude: resolved.lat,
                  longitude: resolved.lng,
                  address: resolved.address,
              }
            : {};
        const facilityInfo = {
            category,
            name,
            location,
            telephone,
            website,
            details,
            ...geo,
        };
        this.bus.emit('SUBMIT_FACILITY_DATA_CHANGE_REQUEST', {
            type,
            userDetails,
            existingFacility,
            facilityInfo,
            suggestedChanges: {
                ...facilityInfo,
                status,
            },
        });
        this.setState({
            disabled: true,
        });
    };

    onChange = (key, value) => {
        this.setState({
            [key]: value,
        });
    };

    renderSuccessfullySubmitted() {
        const { intl } = this.props;
        return (
            <div className="submit-facility-content flex-column">
                <div className="flex-item">
                    <h2>{intl.formatMessage({ id: 'modals.submitFacility.thankYou' })}</h2>
                    <p>{intl.formatMessage({ id: 'modals.submitFacility.youWillGetEmail' })}</p>
                </div>
                <div>
                    <button className="btn-raised" onClick={this.onClose}>
                        {intl.formatMessage({ id: 'done' })}
                    </button>
                </div>
            </div>
        );
    }

    renderCoordinates() {
        const { intl, canPickOnMap } = this.props;
        const resolved = this.resolveLocation();
        return (
            <div className="input-row">
                <FormattedMessage id="modals.submitFacility.coordinates.label" />
                <div className="submit-facility-coordinates">
                    {resolved ? (
                        <span className="submit-facility-coordinates__value">
                            {`${resolved.lat.toFixed(COORDINATE_PRECISION)}, ${resolved.lng.toFixed(
                                COORDINATE_PRECISION,
                            )}`}
                            <span className="submit-facility-coordinates__source">
                                {resolved.fromPin && (
                                    <i className="material-icons-outlined">place</i>
                                )}
                                {intl.formatMessage({
                                    id: resolved.fromPin
                                        ? 'modals.submitFacility.coordinates.fromPin'
                                        : 'modals.submitFacility.coordinates.fromAddress',
                                })}
                            </span>
                        </span>
                    ) : (
                        <span className="submit-facility-coordinates__empty">
                            {intl.formatMessage({ id: 'modals.submitFacility.coordinates.none' })}
                        </span>
                    )}
                    {canPickOnMap && (
                        <button
                            type="button"
                            className="btn-link submit-facility-coordinates__pick"
                            onClick={this.onPickLocationOnMap}
                        >
                            {intl.formatMessage({
                                id: resolved
                                    ? 'modals.submitFacility.coordinates.changeWithPin'
                                    : 'modals.submitFacility.coordinates.setWithPin',
                            })}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    renderForm() {
        const { intl, editMode } = this.props;
        const { category, name, location, telephone, website, status, details, disabled } =
            this.state;
        const SubmitButton = editMode ? SubmitEditedFacilityButton : SubmitMissingFacilityButton;
        const isSubmitDisabled =
            !category ||
            category.length < 3 ||
            !name ||
            name.length < 3 ||
            !location ||
            location.length < 3;
        return (
            <div
                className={classNames('submit-facility-content', 'flex-item', 'flex-column', {
                    disabled,
                })}
            >
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.category.label" />
                    <SimpleDropdown
                        className=""
                        onItemClick={this.onCategoryChanged}
                        items={CATEGORIES.map(s => ({ id: s, text: s }))}
                        selectedItem={{ id: category, text: category }}
                        ariaLabel={intl.formatMessage({
                            id: 'modals.submitFacility.category.placeholder',
                        })}
                    />
                </div>
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.name.label" />
                    <TextInput
                        placeholder={intl.formatMessage({
                            id: 'modals.submitFacility.name.placeholder',
                        })}
                        value={name || ''}
                        onChange={this.onNameChanged}
                    />
                </div>
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.location.label" />
                    <LocationSuggestionInput
                        placeholder={intl.formatMessage({
                            id: 'modals.submitFacility.location.placeholder',
                        })}
                        value={location || ''}
                        onChange={this.onLocationChanged}
                        onSelect={this.onLocationSuggestionSelect}
                    />
                </div>
                {!editMode && this.renderCoordinates()}
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.telephone.label" />
                    <TextInput
                        placeholder={intl.formatMessage({
                            id: 'modals.submitFacility.telephone.placeholder',
                        })}
                        value={telephone || ''}
                        onChange={this.onTelephoneChanged}
                    />
                </div>
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.website.label" />
                    <TextInput
                        placeholder={intl.formatMessage({
                            id: 'modals.submitFacility.website.placeholder',
                        })}
                        value={website || ''}
                        type="url"
                        onChange={this.onWebsiteChanged}
                    />
                </div>
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.status.label" />
                    <SimpleDropdown
                        className=""
                        onItemClick={this.onStatusChanged}
                        items={STATUSES.map(s => ({ id: s, text: s }))}
                        selectedItem={{ id: status, text: status }}
                        ariaLabel={intl.formatMessage({
                            id: 'modals.submitFacility.status.placeholder',
                        })}
                    />
                </div>
                <div className="input-row">
                    <FormattedMessage id="modals.submitFacility.details.label" />
                    <TextInput
                        multiline
                        className="submit-facility-details"
                        placeholder={intl.formatMessage({
                            id: editMode
                                ? 'modals.submitFacility.details.editPlaceholder'
                                : 'modals.submitFacility.details.placeholder',
                        })}
                        value={details || ''}
                        onChange={this.onDetailsChanged}
                    />
                </div>
                <div className="input-row" />
                <div className="submit-facility-actions">
                    <button
                        className="btn-flat btn-flat--gray"
                        onClick={editMode ? this.onReset : this.onClose}
                    >
                        {intl.formatMessage({ id: editMode ? 'reset' : 'close' })}
                    </button>
                    <SubmitButton
                        className="btn-raised"
                        disabled={isSubmitDisabled}
                        onClick={this.onSubmitFacility}
                    >
                        {intl.formatMessage({ id: 'send' })}
                    </SubmitButton>
                </div>
            </div>
        );
    }

    render() {
        const { submittedSuccessfully } = this.state;
        if (submittedSuccessfully) {
            return this.renderSuccessfullySubmitted();
        }
        return this.renderForm();
    }
}

SubmitFacilityPopup.propTypes = {
    intl: PropTypes.object.isRequired,
    // the map's analysis pin at form-open time ({lng, lat}); missing-report mode only
    pinPoint: PropTypes.object,
    // form values carried across a "change location with pin" round trip
    initialValues: PropTypes.object,
    // false when several map instances are open — there is no single map to pin on
    canPickOnMap: PropTypes.bool,
};

SubmitFacilityPopup.defaultProps = {
    pinPoint: undefined,
    initialValues: undefined,
    canPickOnMap: false,
};

export default injectIntl(SubmitFacilityPopup);
